import { useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Task } from '@/types';
import styles from './TaskPanel.module.css';

interface TaskPanelProps {
  tasks: Task[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onTaskClick?: (task: Task) => void;
  onTaskComplete?: (task: Task) => void;
  onTaskDragEnd?: (task: Task) => void;
  onScheduleTask?: (task: Task) => void;
  onSettingsClick?: () => void;
  onAddTask?: () => void;
  onReorder?: (tasks: Task[]) => void;
  variant?: 'sidebar' | 'panel';
}

// Category colors
const categoryColors: Record<string, string> = {
  'deep-work': '#2b6cb0',
  shallow: '#9f7aea',
  personal: '#38a169',
  meeting: '#ed8936',
  urgent: '#e53e3e',
};

function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export function TaskPanel({
  tasks,
  isCollapsed = false,
  onToggleCollapse,
  onTaskClick,
  onTaskComplete,
  onTaskDragEnd,
  onScheduleTask,
  onSettingsClick,
  onAddTask,
  onReorder,
  variant = 'sidebar',
}: TaskPanelProps) {
  const [viewMode, setViewMode] = useState<'list' | 'category'>('list');
  const pendingTasks = tasks.filter((t) => t.status === 'pending');
  const isPanel = variant === 'panel';

  return (
    <motion.aside
      className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${
        isPanel ? styles.panel : ''
      }`}
      initial={false}
      animate={{ opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      aria-label="Task panel"
    >
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.title}>Tasks</span>
        <div className={styles.headerActions}>
          {onAddTask && (
            <motion.button
              className={styles.addBtn}
              onClick={onAddTask}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Add new task"
            >
              +
            </motion.button>
          )}
          {!isPanel && (
            <motion.button
              className={styles.collapseBtn}
              onClick={onToggleCollapse}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!isCollapsed}
            >
              {isCollapsed ? '▶' : '◀'}
            </motion.button>
          )}
        </div>
      </div>

      {/* View toggle */}
      <div className={styles.viewToggle}>
        <button
          className={`${styles.toggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
          onClick={() => setViewMode('list')}
        >
          List
        </button>
        <button
          className={`${styles.toggleBtn} ${viewMode === 'category' ? styles.active : ''}`}
          onClick={() => setViewMode('category')}
        >
          By Category
        </button>
      </div>

      {/* Task list */}
      <div className={styles.taskList}>
        <AnimatePresence mode="popLayout">
          {pendingTasks.length === 0 ? (
            <motion.div
              className={styles.emptyState}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              No pending tasks
            </motion.div>
          ) : (
            <Reorder.Group
              axis="y"
              values={pendingTasks}
              onReorder={(newOrder) => onReorder?.(newOrder)}
              className={styles.reorderGroup}
            >
              {pendingTasks.map((task) => (
                <Reorder.Item
                  key={task.id}
                  value={task}
                  className={styles.taskItem}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  onDragEnd={() => onTaskDragEnd?.(task)}
                >
                  <button
                    className={styles.checkbox}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTaskComplete?.(task);
                    }}
                    aria-label={`Mark "${task.title}" as done`}
                  >
                    <span className={styles.checkboxInner} />
                  </button>
                  <div
                    className={styles.taskContent}
                    onClick={() => onTaskClick?.(task)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && onTaskClick?.(task)}
                  >
                    <div className={styles.taskTitle}>
                      <span
                        className={styles.categoryDot}
                        style={{ background: categoryColors[task.categoryId] || '#999' }}
                        aria-hidden="true"
                      />
                      {task.title}
                    </div>
                    <div className={styles.taskMeta}>
                      {task.estimatedDuration && (
                        <span>Est: {formatDuration(task.estimatedDuration)}</span>
                      )}
                      {task.priority && (
                        <span className={styles[`priority${task.priority}`]}>{task.priority}</span>
                      )}
                    </div>
                  </div>
                  {onScheduleTask && (
                    <button
                      className={styles.scheduleBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        onScheduleTask(task);
                      }}
                      aria-label={`Schedule "${task.title}" as a block`}
                      title="Schedule as block"
                    >
                      ⏱
                    </button>
                  )}
                  <div className={styles.dragHandle} aria-hidden="true">
                    ⋮⋮
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          )}
        </AnimatePresence>
      </div>

      {/* Settings button */}
      <motion.button
        className={styles.settingsBtn}
        onClick={onSettingsClick}
        whileHover={{ backgroundColor: 'var(--color-surface-hover)' }}
      >
        <span aria-hidden="true">⚙️</span>
        <span>Settings</span>
      </motion.button>
    </motion.aside>
  );
}

export default TaskPanel;
